import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Canvas } from '@react-three/fiber';
import { OrbitControls, Sphere, Float } from '@react-three/drei';
import { Brain, Scan, ChefHat, TrendingUp, Camera, Heart, Zap, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';
import SmartNutritionTips from '../components/UI/SmartNutritionTips';

const Home: React.FC = () => {
  const { user } = useAuth();

  const features = [
    {
      icon: <Scan className="w-8 h-8" />,
      title: "AI Food Scanner",
      description: "Real-time food recognition with 95%+ accuracy using advanced computer vision and machine learning.",
      color: "from-purple-500 to-pink-500",
      path: "/food-scanner"
    },
    {
      icon: <ChefHat className="w-8 h-8" />,
      title: "Smart Recipe Generator",
      description: "Transform available ingredients into personalized recipes with 3D cooking animations and nutritional optimization.",
      color: "from-orange-500 to-red-500",
      path: "/recipe-generator"
    },
    {
      icon: <TrendingUp className="w-8 h-8" />,
      title: "Nutrition Analysis",
      description: "Comprehensive nutritional insights with AI-powered recommendations and real-time health metrics tracking.",
      color: "from-green-500 to-teal-500",
      path: "/nutrition-analysis"
    },
    {
      icon: <Camera className="w-8 h-8" />,
      title: "AR Cooking Assistant",
      description: "Immersive augmented reality cooking guidance with voice control and step-by-step 3D visualizations.",
      color: "from-cyan-500 to-blue-500",
      path: "/ar-cooking"
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: "Health Integration",
      description: "Seamless integration with fitness apps and health devices for comprehensive wellness tracking.",
      color: "from-pink-500 to-rose-500",
      path: "/health-integration"
    },
    {
      icon: <Brain className="w-8 h-8" />,
      title: "AI Meal Planner",
      description: "Intelligent meal planning with dietary goal optimization and smart shopping list generation.",
      color: "from-indigo-500 to-purple-500",
      path: "/meal-planner"
    }
  ];

  const stats = [
    { number: "95%+", label: "AI Accuracy", icon: <Zap className="w-6 h-6" /> },
    { number: "50,000+", label: "Food Items Recognized", icon: <Scan className="w-6 h-6" /> },
    { number: "10,000+", label: "Smart Recipes", icon: <ChefHat className="w-6 h-6" /> },
    { number: "100K+", label: "Happy Users", icon: <Heart className="w-6 h-6" /> }
  ];

  const FloatingOrb: React.FC<{ position: [number, number, number]; color: string }> = ({ position, color }) => (
    <Float speed={2} rotationIntensity={1} floatIntensity={2}>
      <Sphere position={position} args={[0.5, 32, 32]}>
        <meshStandardMaterial color={color} transparent opacity={0.6} />
      </Sphere>
    </Float>
  );

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative overflow-hidden py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1 }}
            >
              <div className="flex items-center space-x-2 mb-6">
                <Sparkles className="w-6 h-6 text-yellow-400" />
                <span className="text-purple-300 font-medium">Powered by Advanced AI</span>
              </div>
              
              <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 leading-tight">
                Welcome to{' '}
                <span className="bg-gradient-to-r from-purple-400 via-pink-400 to-cyan-400 bg-clip-text text-transparent">
                  FoodMind
                </span>
              </h1>
              
              <p className="text-xl md:text-2xl text-purple-200 mb-8 leading-relaxed">
                Experience the future of nutrition with AI-powered food recognition, 
                3D recipe visualization, and personalized health insights that adapt to your lifestyle.
              </p>
              
              {user && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.5 }}
                  className="mb-8 p-4 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30"
                >
                  <p className="text-lg text-purple-200">
                    Welcome back, <span className="font-semibold text-white">{user.name}</span>! 
                    Ready to continue your AI-powered nutrition journey?
                  </p>
                </motion.div>
              )}
              
              <div className="flex flex-col sm:flex-row gap-4">
                {user ? (
                  <Link
                    to="/dashboard"
                    className="group bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center space-x-2 transform hover:scale-105"
                  >
                    <span>Go to Dashboard</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/register"
                      className="group bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-300 flex items-center justify-center space-x-2 transform hover:scale-105"
                    >
                      <span>Start Your AI Journey</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <Link
                      to="/food-scanner"
                      className="border-2 border-purple-400 text-purple-300 px-8 py-4 rounded-full font-semibold hover:bg-purple-400/10 transition-all duration-300 flex items-center justify-center space-x-2"
                    >
                      <Scan className="w-5 h-5" />
                      <span>Try Food Scanner</span>
                    </Link>
                  </>
                )}
              </div>
            </motion.div>

            {/* 3D Hero Visual */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 1, delay: 0.3 }}
              className="relative h-96 lg:h-[500px]"
            >
              <Canvas camera={{ position: [0, 0, 8], fov: 75 }}>
                <ambientLight intensity={0.5} />
                <pointLight position={[10, 10, 10]} intensity={1} />
                <pointLight position={[-10, -10, -10]} intensity={0.5} color="#8b5cf6" />
                
                <FloatingOrb position={[0, 0, 0]} color="#8b5cf6" />
                <FloatingOrb position={[3, 2, -2]} color="#ec4899" />
                <FloatingOrb position={[-3, -1, 1]} color="#06b6d4" />
                <FloatingOrb position={[2, -2, 2]} color="#10b981" />
                <FloatingOrb position={[-2, 2, -1]} color="#f59e0b" />
                
                <OrbitControls enableZoom={false} autoRotate autoRotateSpeed={1} />
              </Canvas>
              
              {/* Floating UI Elements */}
              <div className="absolute top-4 left-4">
                <FloatingElement delay={0.5}>
                  <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3">
                    <div className="flex items-center space-x-2">
                      <div className="w-3 h-3 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-white text-sm font-medium">AI Active</span>
                    </div>
                  </div>
                </FloatingElement>
              </div>
              
              <div className="absolute bottom-4 right-4">
                <FloatingElement delay={1}>
                  <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-lg p-3">
                    <div className="text-white text-sm">
                      <div className="font-semibold">95.7% Accuracy</div>
                      <div className="text-purple-300">Food Recognition</div>
                    </div>
                  </div>
                </FloatingElement>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
          >
            {stats.map((stat, index) => (
              <FloatingElement key={index} delay={index * 0.1}>
                <GlassCard className="p-6 text-center" hover={false}>
                  <div className="w-12 h-12 mx-auto mb-4 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center text-white">
                    {stat.icon}
                  </div>
                  <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.number}</div>
                  <div className="text-purple-300 font-medium">{stat.label}</div>
                </GlassCard>
              </FloatingElement>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Revolutionary AI Features
            </h2>
            <p className="text-xl text-purple-200 max-w-3xl mx-auto">
              Discover cutting-edge technology that transforms how you interact with food, 
              nutrition, and cooking through advanced artificial intelligence.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
              >
                <Link to={feature.path} className="block h-full">
                  <GlassCard className="p-8 h-full group cursor-pointer">
                    <div className={`w-16 h-16 rounded-2xl bg-gradient-to-r ${feature.color} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                      {feature.icon}
                    </div>
                    <h3 className="text-xl font-semibold text-white mb-4 group-hover:text-purple-300 transition-colors">
                      {feature.title}
                    </h3>
                    <p className="text-purple-200 leading-relaxed">
                      {feature.description}
                    </p>
                    <div className="mt-6 flex items-center text-purple-400 group-hover:text-purple-300 transition-colors">
                      <span className="font-medium">Explore Feature</span>
                      <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </GlassCard>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <GlassCard className="p-12" glow>
              <div className="flex justify-center mb-6">
                <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Zap className="w-10 h-10 text-white" />
                </div>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
                Ready to Transform Your Nutrition Journey?
              </h2>
              <p className="text-xl text-purple-200 mb-8">
                Join thousands of users who have revolutionized their relationship with food 
                using FoodMind's advanced AI technology and personalized insights.
              </p>
              {!user && (
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/register"
                    className="bg-gradient-to-r from-purple-500 to-pink-500 text-white px-8 py-4 rounded-full font-semibold hover:from-purple-600 hover:to-pink-600 transition-all duration-200 flex items-center justify-center space-x-2"
                  >
                    <span>Start Free Trial</span>
                    <ArrowRight className="w-5 h-5" />
                  </Link>
                  <Link
                    to="/subscription"
                    className="border-2 border-purple-400 text-purple-300 px-8 py-4 rounded-full font-semibold hover:bg-purple-400/10 transition-all duration-200"
                  >
                    View Pricing
                  </Link>
                </div>
              )}
            </GlassCard>
          </motion.div>
        </div>
      </section>

      {/* Smart Nutrition Tips */}
      <SmartNutritionTips />
    </div>
  );
};

export default Home;